import {
    animate,
    state,
    style,
    transition,
    trigger
} from '@angular/animations';
import { ChangeDetectionStrategy, Component, OnInit, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NgIf } from '@angular/common';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { Toast } from 'ngx-toastr';
  
@Component({
    selector: '[liq-toast-component]',
    template: `
        <button *ngIf="options.closeButton" (click)="remove()" class="toast-close-button" aria-label="Close">
            <span aria-hidden="true">&times;</span>
        </button>

        @if (title) {
            @if (translatable(title)) {
                <div [class]="options.titleClass" [attr.aria-label]="title | transloco">
                    {{ title | transloco }} <ng-container *ngIf="duplicatesCount">[{{ duplicatesCount + 1 }}]</ng-container>
                </div>
            } @else {
                <div [class]="options.titleClass" [attr.aria-label]="title">
                    {{ title }} <ng-container *ngIf="duplicatesCount">[{{ duplicatesCount + 1 }}]</ng-container>
                </div>
            }
        }
 
        @if (_message(); as m) {
            @if (options.enableHtml) {
                <div role="alert" [class]="options.messageClass" [innerHTML]="m"></div>
            } @else if (translatable(m)) {
                <div role="alert" [class]="options.messageClass" [attr.aria-label]="m | transloco">
                    {{ m | transloco }}
                </div>
            } @else {
                <div role="alert" [class]="options.messageClass" [attr.aria-label]="m">
                    {{ m }}
                </div>
            }
        }
        <div *ngIf="options.progressBar">
            <div class="toast-progress" [style.width]="width + '%'"></div>
        </div>
    `,
    animations: [
        trigger('flyInOut', [
            state('inactive', style({ opacity: 0 })),
            state('active', style({ opacity: 1 })),
            state('removed', style({ opacity: 0 })),
            transition('inactive => active', animate('{{ easeTime }}ms {{ easing }}')),
            transition('active => removed', animate('{{ easeTime }}ms {{ easing }}'))
        ])
    ],
    preserveWhitespaces: false,
    standalone: true,
    imports: [NgIf, TranslocoModule],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class LibraryIQToastComponent extends Toast implements OnInit {
    private readonly transloco = inject(TranslocoService);

    /**
     * Ticks whenever a language or a lazy translation file loads. Read while
     * deciding whether to translate, so a toast raised before its translations
     * arrived re-checks rather than leaving a raw key on screen.
     */
    private readonly translationEvents = toSignal(this.transloco.events$);

    protected _message = signal('');
    protected useTranslations = signal(true);

    ngOnInit(): void {
        this._message.set(this.message);
    }

    updateMessage(message: string): void {
        this.message = message;
        this._message.set(message);
    }

    disableTranslations(): void {
        this.useTranslations.set(false);
    }

    /**
     * Whether a title or message should be run through transloco.
     *
     * Callers hand us either a translation key or a finished sentence — some
     * have to translate up front, because the toast component cannot pass
     * interpolation params to the pipe. Looking a finished sentence up as if it
     * were a key makes transloco report it missing, so only translate text the
     * active (or default) language actually has a key for. This is the same
     * lookup `TranslocoService.translate` performs, so the two never disagree.
     */
    protected translatable(text: string | undefined): boolean {
        // Read first so the dependency is always registered.
        this.translationEvents();
        if (!text || !this.useTranslations()) return false;
        return this.hasKey(this.transloco.getActiveLang(), text)
            || this.hasKey(this.transloco.getDefaultLang(), text);
    }

    private hasKey(lang: string, key: string): boolean {
        // Mirrors transloco's own falsy check, so an empty value counts as absent.
        return !!this.transloco.getTranslation(lang)[key];
    }
}
